import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { deleteLesson } from "../../graphql/mutations";
import { listLessons } from "../../../src/graphql/queries";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Grid2, Typography, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LessonsTable from "./LessonsTable";

const ManageLessons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [lessons, setLessons] = useState([]);
  const [open, setOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const fetchLessons = async () => {
      const client = generateClient();
      try {
        const result = await client.graphql({
          query: listLessons,
          variables: {
            limit: 100,
          },
        });
        const fetchedLessons = result.data.listLessons.items;
        // Sort by order ascending (1, 2, 3, ...)
        const sortedLessons = fetchedLessons.sort((a, b) => parseInt(a.order) - parseInt(b.order));
        setLessons(sortedLessons);
      } catch (error) {
        setSnackbarMessage("Error fetching lessons.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };

    fetchLessons();
  }, []);

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSnackbarMessage(location.state.successMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Clear the state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleAddLesson = () => {
    navigate("/settings/lessons/new");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenModal = (lessonId) => {
    setLessonToDelete(lessonId);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setLessonToDelete(null);
  };

  const handleConfirmDelete = async () => {
    const client = generateClient();
    try {
      await client.graphql({
        query: deleteLesson,
        variables: { input: { id: lessonToDelete } },
      });
      setLessons((prevLessons) => prevLessons.filter((lesson) => lesson.id !== lessonToDelete));
      setSnackbarMessage("Lesson successfully deleted.");
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage("Error deleting lesson.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      handleCloseModal();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const renderTitle = () => {
    return (
      <Grid2 xs={12} display="flex" alignItems="center">
        <IconButton
          variant="contained"
          color="primary"
          onClick={handleBack}
          sx={{ marginRight: 2 }}
        >
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        <Typography variant="h3">Manage Lessons</Typography>
      </Grid2>
    );
  };

  const renderAddLessonButton = () => {
    return (
      <Grid2 xs={12} sm={6} md={4}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleAddLesson}
        >
          Add Lesson
        </Button>
      </Grid2>
    );
  };

  const renderDeleteDialog = () => {
    return (
      <Dialog
        open={open}
        onClose={handleCloseModal}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lesson?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid2
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        {renderTitle()}
        {renderAddLessonButton()}
        <LessonsTable lessons={lessons} onDeleteLesson={handleOpenModal} />
      </Grid2>
      {renderDeleteDialog()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: 8 }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageLessons;